import { z } from 'zod';

/**
 * The identity credential an enterprise IdP hands back at SSO login, kept so
 * Reflex can later mint ID-JAG assertions on the user's behalf.
 *
 * This is the outbound half of `docs/internal/plans/id-jag-cross-app-access.md`.
 * Under ID-JAG the chain to a third-party access token is
 * `access token → ID-JAG → the user's identity credential`, and an agent that
 * runs for hours — or resumes after a suspend — reaches the end of that chain
 * with nobody around to re-authenticate. Which is why `refreshToken` matters
 * more than it looks: an ID token expires in minutes, and refreshing it at the
 * IdP is the only way the chain survives its owner going to sleep.
 *
 * Stored encrypted at the user tier. One record per user replaces the N
 * per-server refresh tokens the DCR flow accumulates.
 */
export const IdentityAssertionRecordSchema = z.object({
  /** Issuer that minted these tokens; the audience for later exchanges. */
  issuer: z.string().min(1),
  /** OIDC `sub`, retained so a re-login for a different subject is detectable. */
  subject: z.string().min(1),
  idToken: z.string().min(1),
  /** Epoch ms the ID token expires, from its own `exp`. */
  idTokenExpiresAt: z.number().optional(),
  /**
   * OIDC refresh token, present only when the IdP issued one (which needs
   * the `offline_access` scope). Its absence is what makes unattended agents
   * fail closed rather than silently hand out a dead token.
   */
  refreshToken: z.string().optional(),
  updatedAt: z.number(),
});
export type IdentityAssertionRecord = z.infer<typeof IdentityAssertionRecordSchema>;

/**
 * What an OAuth provider plugin hands the host at sign-in. The host owns
 * storage; the plugin only reports what the IdP returned.
 */
export interface IdentityAssertionInput {
  issuer: string;
  subject: string;
  idToken: string;
  idTokenExpiresAt?: number;
  refreshToken?: string;
}
